"use client";

import React from "react";
import { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Grid2,
  useTheme,
  Avatar,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useGetNcrKpiSummaryQuery } from "store/slices/apis/qualityDashboardSlice";
import moment from "moment";
import { MdOutlineArrowOutward } from "react-icons/md";
import { ArrowDownward, ArrowOutward, ArrowUpward } from "@mui/icons-material";
import NcrPopup from "./NcrPopup";
import ProjectStatusOverviewHead from "../../../../components/ProjectStatusOverviewHead";
import { ReactComponent as ArrowIcon } from "assets/PwC_Funct_Icons_ArrowIncrease_Outline_Black_RGB.svg"
import { ReactComponent as ArrowUpIcon } from "assets/PwC_Funct_Icons_ArrowUp_Outline_Black_RGB.svg"
import { ReactComponent as ArrowDownIcon } from "assets/PwC_Funct_Icons_ArrowDown_Outline_Black_RGB.svg"

const NCRKPI = () => {
  const gridItemProps = { xs: 12, sm: 6, md: 3 };
  const theme = useTheme();
  const { projectId } = useParams();
  const { data, isLoading } = useGetNcrKpiSummaryQuery({ projectId });
  const kpiRaw = data?.data || {};
  const [popupData, setPopupData] = useState(null);
  const [open, setOpen] = useState(false);

  const lastUpdated = data?.data?.lastUpdated;

  const cardsData = [
    {
      title: "Total NCR",
      value: isLoading ? "--" : kpiRaw?.totalNcr,
      rawChange: kpiRaw?.totalNcrGrowth,
      change: isLoading
        ? ""
        : `${kpiRaw?.totalNcrGrowth >= 0 ? "+" : ""}${kpiRaw?.totalNcrGrowth}%`,
      //isPositive: isLoading ? true : parseFloat(kpiRaw?.totalNcrGrowth) >= 0,
      color: "#9ED3CC",
      iconColor: "#E0F0E4",
      dotColor: "#CE412D",
      textColor: "#377E36",
      valueColor: "#000",
      timeFrame: "this month",
      status: "Total NCR",
      isPositive: isLoading
        ? true
        : parseFloat(kpiRaw?.totalNcrGrowth ?? 0) > 0
          ? false
          : true,
      arrow: `${parseFloat(kpiRaw?.totalNcrGrowth ?? 0) >= 0 ? "up" : "down"}`,
      absoluteChange: isNaN(kpiRaw?.totalNcrGrowth)
        ? "0%"
        : kpiRaw?.totalNcrGrowth === -100
          ? "0%"
          : `${Math.abs(kpiRaw?.totalNcrGrowth ?? 10)}%`,
    },
    {
      title: "Total Open NCR",
      value: isLoading ? "--" : kpiRaw?.totalOpen,
      rawChange: kpiRaw?.openGrowth,
      change: isLoading
        ? ""
        : `${kpiRaw?.openGrowth >= 0 ? "+" : ""}${kpiRaw?.openGrowth}%`,
      isPositive: isLoading ? true : parseFloat(kpiRaw?.openGrowth) >= 0,
      color: "#9ED3CC",
      iconColor: "#E0F0E4",
      dotColor: "#CE412D",
      textColor: "#377E36",
      valueColor: "#000",
      timeFrame: "this month",
      status: "Open Ncr",
      isPositive: isLoading
        ? true
        : parseFloat(kpiRaw?.openGrowth ?? 0) > 0
          ? false
          : true,
      arrow: `${parseFloat(kpiRaw?.openGrowth ?? 0) >= 0 ? "up" : "down"}`,
      absoluteChange: isNaN(kpiRaw?.openGrowth)
        ? "0%"
        : kpiRaw?.openGrowth === -100
          ? "0%"
          : `${Math.abs(kpiRaw?.openGrowth ?? 0)}%`,
    },
    {
      title: "Total Closed NCR",
      value: isLoading ? "--" : kpiRaw?.totalClosed,
      rawChange: kpiRaw?.closedGrowth,
      change: isLoading
        ? ""
        : `${kpiRaw?.closedGrowth >= 0 ? "+" : ""}${kpiRaw?.closedGrowth}%`,
      isPositive: isLoading ? true : parseFloat(kpiRaw?.closedGrowth) >= 0,
      color: "#9ED3CC",
      iconColor: "#E0F0E4",
      dotColor: "#CE412D",
      textColor: "#377E36",
      valueColor: "#000",
      timeFrame: "this month",
      status: "Closed Ncr",
      isPositive: isLoading
        ? true
        : parseFloat(kpiRaw?.closedGrowth ?? 0) > 0
          ? true
          : false,
      arrow: `${parseFloat(kpiRaw?.closedGrowth ?? 0) >= 0 ? "up" : "down"}`,
      absoluteChange: isNaN(kpiRaw?.closedGrowth)
        ? "0%"
        : kpiRaw?.closedGrowth === -100
          ? "0%"
          : `${Math.abs(kpiRaw?.closedGrowth ?? 0)}%`,
    },
    {
      title: "Avg Duration of Closure",
      value: isLoading ? "--" : kpiRaw?.avgClosureDuration,
      rawChange: kpiRaw?.durationGrowth,
      change: isLoading
        ? ""
        : `${kpiRaw?.durationGrowth >= 0 ? "+" : ""}${kpiRaw?.durationGrowth}%`,
      isPositive: isLoading ? true : parseFloat(kpiRaw?.durationGrowth) >= 0,
      color: "#9ED3CC",
      iconColor: "#E0F0E4",
      dotColor: "#CE412D",
      textColor: "#377E36",
      valueColor: "#CE412D",
      timeFrame: "this month",
      status: "Avg Duration",
      //   isPositive: isLoading
      //     ? true
      //     : parseFloat(kpi?.totalManHours?.growthPercentage ?? 0) > 0
      //     ? true
      //     : false,
      //   arrow: `${
      //     parseFloat(kpi?.totalManHours?.growthPercentage ?? 0) >= 0
      //       ? "up"
      //       : "down"
      //   }`,
      absoluteChange: isNaN(kpiRaw?.durationGrowth)
        ? "0%"
        : kpiRaw?.durationGrowth === -100
          ? "0%"
          : `${Math.abs(kpiRaw?.durationGrowth ?? 0)}%`,
    },
  ];

  return (
    <>
      <Grid2 container spacing={3} sx={{ mt: "20px" }}>
        <ProjectStatusOverviewHead
          lastModified={lastUpdated}
          marginBottm={"30px"}
        />

        {cardsData.map((card, index) => (
          <Grid2 size={{ xs: 12, md: 3 }}>
            <Card
              sx={{
                boxShadow: "none",
                border: "1px solid #25252517",
                borderRadius: "3px",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ pl: "20px", pr: "20px", pt: "17px", pb: "17px" }}
              >
                <Box display="flex" alignItems="center" sx={{ mr: 0 }}>
                  <Typography
                    sx={{
                      bgcolor: card.color,
                      width: "45px",
                      height: "45px",
                      mr: 2,
                    }}
                  />
                  <Box>
                    <Box display="flex" alignItems="center">
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          mr: 1,
                          lineHeight: "100%",
                          fontSize: "18px",
                          color: card.valueColor,
                        }}
                      >
                        {card.value}
                      </Typography>
                      {card.status && (
                        <Chip
                          label={card.absoluteChange}
                          size="small"
                          sx={{
                            backgroundColor: card.iconColor,
                            color:
                              card.isPositive === false
                                ? card.absoluteChange === "0%"
                                  ? card.textColor
                                  : card.dotColor
                                : card.textColor,
                            fontWeight: 500,
                            fontSize: "12px",
                            borderRadius: "4px",
                            width: "45px",
                            height: "20px",
                          }}
                          icon={
                            card.absoluteChange !== "0%" &&
                            // (card.arrow === "up" ? (
                            //   <ArrowUpward
                            //     sx={{
                            //       fontSize: "12px !important",
                            //       color: card.isPositive
                            //         ? `${card.textColor} !important`
                            //         : `${card.dotColor} !important`,
                            //     }}
                            //   />
                            // ) : (
                            //   <ArrowDownward
                            //     sx={{
                            //       fontSize: "12px !important",
                            //       color: card.isPositive
                            //         ? `${card.textColor} !important`
                            //         : `${card.dotColor} !important`,
                            //     }}
                            //   />
                            // ))
                            (
                              card.arrow === "up" ? (
                                <ArrowUpIcon
                                  style={{
                                    height: "12px",
                                    width: "12px",
                                    fill: card.isPositive ? card.textColor : card.dotColor,
                                  }}
                                />
                              ) : (
                                <ArrowDownIcon
                                  style={{
                                    height: "12px",
                                    width: "12px",
                                    fill: card.isPositive ? card.textColor : card.dotColor,
                                  }}
                                />
                              )
                            )

                          }
                        />
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          ml: 1,
                          fontSize: "13px",
                          fontWeight: 400,
                          lineHeight: "24px",
                          color: "#474747",
                        }}
                      >
                        {card.timeFrame}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: "5px",
                        fontSize: "13px",
                        fontWeight: 500,
                        lineHeight: "100%",
                        color: "#252525",
                      }}
                    >
                      {card.title}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  {card.status && (
                    <Avatar
                      sx={{
                        bgcolor: "#fff",
                        width: "37px",
                        height: "37px",
                        border: "1px solid #d3d3d3",
                      }}
                    >
                      {/* <MdOutlineArrowOutward
                                             style={{
                                               color: "#252525",
                                               fontSize: "20px",
                                               cursor: "pointer",
                                              
                                             }}
                                             onClick={() => {
                                               setPopupData(card);
                                               setOpen(true);
                                             }}
                                           /> */}
                      <ArrowIcon
                        style={{
                          color: "#252525",
                          fontSize: "20px",
                          cursor: "pointer",
                          height: "13px",
                          stroke: "#252525",
                          strokeWidth: "6"
                        }}
                        onClick={() => {
                          setPopupData(card);
                          setOpen(true);
                        }}
                      />
                    </Avatar>
                  )}
                </Box>
              </Box>
            </Card>
          </Grid2>
        ))}
      </Grid2>

      {open && (
        <NcrPopup
          title={popupData.title}
          value={popupData.value}
          status={popupData.status}
          onOpen={open}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

export default NCRKPI;
